<div class="keyword-research-test-container">
  <header class="test-header">
    <h1>🔍 Keyword Research Test</h1>
    <p>Test DataForSEO keyword research endpoints</p>
  </header>

  <!-- NEW SECTION: Topic-based batch search -->
  <div class="batch-search-section">
    <h2>Topic Entity Discovery</h2>
    <p class="section-description">Enter a domain and topic to discover keywords for all merged + related topics</p>
    
    <div class="test-form">
      <div class="form-group">
        <label for="domain-input">Domain:</label>
        <input
          id="domain-input"
          type="text"
          [(ngModel)]="domain"
          placeholder="e.g., marker.io"
          class="keyword-input"
        />
      </div>

      <div class="form-group">
        <label for="topic-slug-input">Topic Slug:</label>
        <input
          id="topic-slug-input"
          type="text"
          [(ngModel)]="topicSlug"
          placeholder="e.g., website-feedback"
          class="keyword-input"
        />
      </div>

      <div class="form-group">
        <label for="batch-location-input">Location Code:</label>
        <input
          id="batch-location-input"
          type="number"
          [(ngModel)]="locationCode"
          class="number-input"
        />
        <small>2840 = United States</small>
      </div>

      <div class="form-group">
        <label for="batch-language-input">Language:</label>
        <input
          id="batch-language-input"
          type="text"
          [(ngModel)]="languageName"
          class="text-input"
        />
      </div>

      <div class="form-group">
        <label for="batch-depth-input">Depth (0-4):</label>
        <input
          id="batch-depth-input"
          type="number"
          [(ngModel)]="depth"
          min="0"
          max="4"
          class="number-input"
        />
      </div>

      <div class="form-group">
        <label for="batch-limit-input">Limit per topic:</label>
        <input
          id="batch-limit-input"
          type="number"
          [(ngModel)]="limit"
          min="1"
          max="1000"
          class="number-input"
        />
      </div>

      <div class="form-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            [(ngModel)]="filterBySearchVolume"
            class="checkbox-input"
          />
          Filter by minimum search volume
        </label>
        <input
          *ngIf="filterBySearchVolume"
          type="number"
          [(ngModel)]="minSearchVolume"
          min="0"
          class="number-input"
          style="margin-top: 0.5rem;"
        />
      </div>

      <button 
        (click)="searchTopicsForKeywords()" 
        [disabled]="batchLoading || !domain.trim() || !topicSlug.trim()"
        class="test-button"
      >
        {{ batchLoading ? 'Searching...' : 'Discover Keywords for All Topics' }}
      </button>

      <div *ngIf="batchError" class="error-message">{{ batchError }}</div>
    </div>

    <!-- Batch Results -->
    <div *ngIf="batchResults.length > 0 && !batchLoading" class="batch-results">
      <div class="results-header">
        <h3>Discovery Results</h3>
        <div class="results-meta">
          <span>Topics Analyzed: {{ batchResults.length }}</span>
          <span>Unique Keywords: {{ getTotalUniqueKeywords() }}</span>
          <span *ngIf="clusteredKeywords.length > 0">
            Clusters: {{ filteredClusteredKeywords.length }}<span *ngIf="clusteredKeywords.length > filteredClusteredKeywords.length">/{{ clusteredKeywords.length }}</span>
          </span>
          <span>Total Cost: ${{ totalCost.toFixed(4) }}</span>
          <span *ngIf="rankedKeywordsMetadata" class="ranked-keywords-info">
            Ranked Keywords: {{ rankedKeywordsMetadata.keywordCount }} 
            <span [class.from-db]="rankedKeywordsMetadata.dataSource === 'database'" 
                  [class.from-api]="rankedKeywordsMetadata.dataSource === 'api'"
                  class="data-source-badge">
              ({{ rankedKeywordsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
            </span>
            <span *ngIf="rankedKeywordsMetadata.lastUpdated" class="last-updated">
              • Updated: {{ rankedKeywordsMetadata.lastUpdated | date:'short' }}
            </span>
          </span>
        </div>
        <div class="view-toggle" *ngIf="clusteredKeywords.length > 0">
          <button 
            (click)="showClusteredView = false" 
            [class.active]="!showClusteredView"
            class="toggle-button"
          >
            Topic View
          </button>
          <button 
            (click)="showClusteredView = true" 
            [class.active]="showClusteredView"
            class="toggle-button"
          >
            Clustered View
          </button>
        </div>
      </div>

      <!-- Clustered View -->
      <div *ngIf="showClusteredView && filteredClusteredKeywords.length > 0" class="clustered-view">
        <div class="clustered-results-info" *ngIf="clusteredKeywords.length > filteredClusteredKeywords.length">
          <small>Showing {{ filteredClusteredKeywords.length }} of {{ clusteredKeywords.length }} clusters (similarity ≥ {{ (similarityThreshold * 100).toFixed(0) }}%)</small>
        </div>
        <div class="clustered-results">
          <div *ngFor="let cluster of filteredClusteredKeywords" class="cluster-wrapper">
            <span 
              class="keyword-tag cluster-header"
              [class.covered]="cluster.isCovered"
              [class.not-covered]="!cluster.isCovered"
              [class.expanded]="isClusterExpanded(cluster.clusterName)"
              (click)="toggleClusterExpansion(cluster.clusterName)"
              [title]="cluster.totalKeywordVariants + ' keyword variants' + (cluster.totalUrlCount > 0 ? ', ' + cluster.totalUrlCount + ' URLs' : '') + (cluster.similarityScore !== undefined ? ', Similarity: ' + (cluster.similarityScore * 100).toFixed(0) + '%' : '') + ' - Click to expand'"
            >
              {{ cluster.clusterName }}
              <span *ngIf="cluster.isCovered && cluster.totalUrlCount > 0" class="url-count">({{ cluster.totalUrlCount }})</span>
              <span *ngIf="cluster.similarityScore !== undefined" 
                    class="similarity-score"
                    [class.high-similarity]="cluster.similarityScore >= 0.7"
                    [class.medium-similarity]="cluster.similarityScore >= 0.4 && cluster.similarityScore < 0.7"
                    [class.low-similarity]="cluster.similarityScore < 0.4">
                {{ (cluster.similarityScore * 100).toFixed(0) }}%
              </span>
              <span class="expand-icon">{{ isClusterExpanded(cluster.clusterName) ? '▼' : '▶' }}</span>
            </span>
            <div *ngIf="isClusterExpanded(cluster.clusterName)" class="expanded-cluster">
              <div *ngFor="let dedupedKw of cluster.keywords" class="deduplicated-keyword">
                <span 
                  class="keyword-tag canonical-keyword"
                  [class.covered]="dedupedKw.isCovered"
                  [class.not-covered]="!dedupedKw.isCovered"
                  [title]="'Canonical: ' + dedupedKw.canonicalKeyword + ', Search Volume: ' + dedupedKw.totalSearchVolume + (dedupedKw.isCovered ? (dedupedKw.bestRankingPosition ? ', Ranking: #' + dedupedKw.bestRankingPosition : ', URLs: ' + dedupedKw.totalUrlCount) : '')"
                >
                  {{ dedupedKw.canonicalKeyword }}
                  <span *ngIf="dedupedKw.isCovered && dedupedKw.bestRankingPosition" class="ranking-badge">#{{ dedupedKw.bestRankingPosition }}</span>
                  <span *ngIf="dedupedKw.isCovered && dedupedKw.totalUrlCount && !dedupedKw.bestRankingPosition" class="url-count">({{ dedupedKw.totalUrlCount }})</span>
                  <span class="variant-count">({{ dedupedKw.variants.length }} variant{{ dedupedKw.variants.length !== 1 ? 's' : '' }})</span>
                </span>
                <div class="keyword-variants">
                  <span 
                    *ngFor="let variant of dedupedKw.variants" 
                    class="keyword-tag variant-keyword"
                    [class.covered]="variant.isCovered"
                    [class.not-covered]="!variant.isCovered"
                    [title]="'Search Volume: ' + variant.search_volume + ', CPC: $' + variant.cpc + (variant.isCovered ? (variant.rankingPosition ? ', Ranking: #' + variant.rankingPosition : ', URLs: ' + (variant.urlCount || 0)) : '')"
                  >
                    {{ variant.keyword }}
                    <span *ngIf="variant.isCovered && variant.rankingPosition" class="ranking-badge">#{{ variant.rankingPosition }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Topic View (existing) -->
      <div *ngIf="!showClusteredView" class="topic-view">
      <div class="topic-results" *ngFor="let topicResult of batchResults">
        <div class="topic-header">
          <h4>{{ topicResult.topicLabel }}</h4>
          <span class="topic-type">{{ topicResult.topicType }}</span>
        </div>
        
        <div class="keyword-sections">
          <div class="keyword-section" *ngIf="topicResult.groupedRelatedKeywords.length > 0">
            <h5>
              Related Keywords ({{ topicResult.groupedRelatedKeywords.length }} groups)
              <span *ngIf="topicResult.relatedKeywordsMetadata" class="data-source-info">
                <span [class.from-db]="topicResult.relatedKeywordsMetadata.dataSource === 'database'" 
                      [class.from-api]="topicResult.relatedKeywordsMetadata.dataSource === 'api'"
                      class="data-source-badge">
                  ({{ topicResult.relatedKeywordsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
                </span>
              </span>
            </h5>
            <div class="keyword-groups">
              <div *ngFor="let group of topicResult.groupedRelatedKeywords" class="keyword-group-wrapper">
                <span 
                  class="keyword-tag group-header"
                  [class.covered]="group.isCovered"
                  [class.not-covered]="!group.isCovered"
                  [class.expanded]="isGroupExpanded(topicResult.topicSlug, 'related', group.groupName)"
                  (click)="toggleGroupExpansion(topicResult.topicSlug, 'related', group.groupName)"
                  [title]="group.totalCount + ' keywords' + (group.totalUrlCount > 0 ? ', ' + group.totalUrlCount + ' URLs' : '') + ' - Click to expand'"
                >
                  {{ group.groupName }}
                  <span *ngIf="group.isCovered && group.totalUrlCount > 0" class="url-count">({{ group.totalUrlCount }})</span>
                  <span class="expand-icon">{{ isGroupExpanded(topicResult.topicSlug, 'related', group.groupName) ? '▼' : '▶' }}</span>
                </span>
                <div *ngIf="isGroupExpanded(topicResult.topicSlug, 'related', group.groupName)" class="expanded-keywords">
                  <span 
                    *ngFor="let kw of group.keywords" 
                    class="keyword-tag expanded-keyword"
                    [class.covered]="kw.isCovered"
                    [class.not-covered]="!kw.isCovered"
                    [title]="'Search Volume: ' + kw.search_volume + ', CPC: $' + kw.cpc + (kw.isCovered ? (kw.rankingPosition ? ', Ranking: #' + kw.rankingPosition + (kw.rankingUrl ? ' (' + kw.rankingUrl + ')' : '') : ', URLs: ' + kw.urlCount) : '')"
                  >
                    {{ kw.keyword }}
                    <span *ngIf="kw.isCovered && kw.rankingPosition" class="ranking-badge">#{{ kw.rankingPosition }}</span>
                    <span *ngIf="kw.isCovered && kw.urlCount && !kw.rankingPosition" class="url-count">({{ kw.urlCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="keyword-section" *ngIf="topicResult.groupedSuggestions.length > 0">
            <h5>
              Keyword Suggestions ({{ topicResult.groupedSuggestions.length }} groups)
              <span *ngIf="topicResult.suggestionsMetadata" class="data-source-info">
                <span [class.from-db]="topicResult.suggestionsMetadata.dataSource === 'database'" 
                      [class.from-api]="topicResult.suggestionsMetadata.dataSource === 'api'"
                      class="data-source-badge">
                  ({{ topicResult.suggestionsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
                </span>
              </span>
            </h5>
            <div class="keyword-groups">
              <div *ngFor="let group of topicResult.groupedSuggestions" class="keyword-group-wrapper">
                <span 
                  class="keyword-tag group-header"
                  [class.covered]="group.isCovered"
                  [class.not-covered]="!group.isCovered"
                  [class.expanded]="isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName)"
                  (click)="toggleGroupExpansion(topicResult.topicSlug, 'suggestions', group.groupName)"
                  [title]="group.totalCount + ' keywords' + (group.totalUrlCount > 0 ? ', ' + group.totalUrlCount + ' URLs' : '') + ' - Click to expand'"
                >
                  {{ group.groupName }}
                  <span *ngIf="group.isCovered && group.totalUrlCount > 0" class="url-count">({{ group.totalUrlCount }})</span>
                  <span class="expand-icon">{{ isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName) ? '▼' : '▶' }}</span>
                </span>
                <div *ngIf="isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName)" class="expanded-keywords">
                  <span 
                    *ngFor="let kw of group.keywords" 
                    class="keyword-tag expanded-keyword"
                    [class.covered]="kw.isCovered"
                    [class.not-covered]="!kw.isCovered"
                    [title]="'Search Volume: ' + kw.search_volume + ', CPC: $' + kw.cpc + (kw.isCovered ? (kw.rankingPosition ? ', Ranking: #' + kw.rankingPosition + (kw.rankingUrl ? ' (' + kw.rankingUrl + ')' : '') : ', URLs: ' + kw.urlCount) : '')"
                  >
                    {{ kw.keyword }}
                    <span *ngIf="kw.isCovered && kw.rankingPosition" class="ranking-badge">#{{ kw.rankingPosition }}</span>
                    <span *ngIf="kw.isCovered && kw.urlCount && !kw.rankingPosition" class="url-count">({{ kw.urlCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- EXISTING SECTION: Single keyword search -->
  <div class="single-search-section">
    <h2>Single Keyword Search</h2>
    <p class="section-description">Test individual keyword research endpoints</p>
    
    <div class="test-form">
    <div class="form-group">
      <label for="keyword-input">{{ method === 'keywords-for-site' ? 'Domain:' : 'Keyword:' }}</label>
      <input
        id="keyword-input"
        type="text"
        [(ngModel)]="keyword"
        [placeholder]="method === 'keywords-for-site' ? 'e.g., marker.io' : 'e.g., website feedback'"
        (keyup.enter)="testKeywordResearch()"
        class="keyword-input"
      />
      <small *ngIf="method === 'keywords-for-site'">Enter domain name without https://</small>
    </div>

    <div class="form-group">
      <label for="method-select">Method:</label>
      <select id="method-select" [(ngModel)]="method" class="method-select">
        <option value="related">Related Keywords</option>
        <option value="ideas">Keyword Ideas</option>
        <option value="suggestions">Keyword Suggestions</option>
        <option value="keywords-for-site">Keywords For Site</option>
      </select>
    </div>

    <div class="form-group" *ngIf="method === 'related'">
      <label for="depth-input">Depth (0-4):</label>
      <input
        id="depth-input"
        type="number"
        [(ngModel)]="depth"
        min="0"
        max="4"
        class="number-input"
      />
      <small>Higher depth = more keywords (up to 4680)</small>
    </div>

    <div class="form-group">
      <label for="limit-input">Limit:</label>
      <input
        id="limit-input"
        type="number"
        [(ngModel)]="limit"
        min="1"
        max="1000"
        class="number-input"
      />
    </div>

    <div class="form-group">
      <label for="location-input">Location Code:</label>
      <input
        id="location-input"
        type="number"
        [(ngModel)]="locationCode"
        class="number-input"
      />
      <small>2840 = United States</small>
    </div>

    <div class="form-group">
      <label for="language-input">Language:</label>
      <input
        id="language-input"
        type="text"
        [(ngModel)]="languageName"
        class="text-input"
      />
    </div>

    <div class="form-group">
      <label class="checkbox-label">
        <input
          type="checkbox"
          [(ngModel)]="filterBySearchVolume"
          class="checkbox-input"
        />
        Filter by minimum search volume
      </label>
      <input
        *ngIf="filterBySearchVolume"
        type="number"
        [(ngModel)]="minSearchVolume"
        min="0"
        class="number-input"
        style="margin-top: 0.5rem;"
      />
      <small *ngIf="filterBySearchVolume">Only show keywords with search volume greater than this value</small>
    </div>

    <button 
      (click)="testKeywordResearch()" 
      [disabled]="loading || !keyword.trim()"
      class="test-button"
    >
      {{ loading ? 'Loading...' : 'Test Keyword Research' }}
    </button>

    <div *ngIf="error" class="error-message">{{ error }}</div>
  </div>

  <div *ngIf="results && !loading" class="results-section">
    <div class="results-header">
      <h2>Results</h2>
      <div class="results-meta">
        <span>Total: {{ getTotalCount() }}</span>
        <span>Returned: {{ getItemsCount() }}</span>
        <span>Cost: ${{ getCost().toFixed(4) }}</span>
        <span *ngIf="resultsMetadata" class="data-source-info">
          Data Source: 
          <span [class.from-db]="resultsMetadata.dataSource === 'database'" 
                [class.from-api]="resultsMetadata.dataSource === 'api'"
                class="data-source-badge">
            {{ resultsMetadata.dataSource === 'database' ? 'Keyword Research Test Database' : (resultsMetadata.dataSource === 'api' ? 'API' : 'Unknown') }}
          </span>
          <span *ngIf="resultsMetadata.lastUpdated" class="last-updated">
            • Updated: {{ resultsMetadata.lastUpdated | date:'short' }}
          </span>
        </span>
      </div>
    </div>

    <div class="results-table">
      <table>
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Search Volume</th>
            <th>Competition</th>
            <th>Competition Level</th>
            <th>CPC</th>
            <th>Keyword Difficulty</th>
            <th>Categories</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let keyword of getKeywordsFromResults()">
            <td class="keyword-cell">{{ keyword.keyword }}</td>
            <td>{{ keyword.search_volume.toLocaleString() }}</td>
            <td>{{ (keyword.competition * 100).toFixed(1) }}%</td>
            <td>
              <span [class]="'competition-' + keyword.competition_level.toLowerCase()">
                {{ keyword.competition_level }}
              </span>
            </td>
            <td>${{ keyword.cpc.toFixed(2) }}</td>
            <td>{{ keyword.keyword_difficulty !== undefined ? keyword.keyword_difficulty : 'N/A' }}</td>
            <td>{{ keyword.categories?.length || 0 }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="raw-results">
      <details>
        <summary>Raw API Response</summary>
        <pre>{{ results | json }}</pre>
      </details>
    </div>
  </div>
  </div>
</div>

